import React, { useEffect, useState, useCallback } from "react";
import {
  Grid, Card, CardContent, Typography, Tabs, Tab, Box,
  Table, TableBody, TableCell, TableHead, TableRow, LinearProgress,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DeltaKpi from "../../components/KpiCard";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  card: { textAlign: "center", padding: theme.spacing(2), height: "100%" },
  big: { fontSize: 28, fontWeight: 600 },
  label: { color: theme.palette.text.secondary, fontSize: 12, textTransform: "uppercase" },
  bar: { height: 8, borderRadius: 4, marginTop: 4 },
  tabs: { flexBasis: "100%", minHeight: 40, marginBottom: theme.spacing(-0.5) },
  positive: { color: theme.palette.success.main },
  negative: { color: theme.palette.error.main },
}));

const fmtMoney = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtNum = v => Number(v || 0).toLocaleString("pt-BR");

/**
 * Cartao de indicador.
 *
 * `raw` existe porque o cartao de Conversao passava a string "42%" e caia em
 * fmtNum -> Number("42%") -> NaN, exibindo "NaN" no dashboard. Valor ja
 * formatado entra por `raw` e sai como veio.
 */
const Stat = ({ label, value, tone, money, raw }) => {
  const cls = useStyles();
  const toneClass =
    tone === "positive" ? cls.positive : tone === "negative" ? cls.negative : undefined;
  return (
    <Card className={cls.card}>
      <CardContent>
        <div className={cls.label}>{label}</div>
        <div className={`${cls.big} ${toneClass || ""}`}>
          {raw ? value : money ? fmtMoney(value) : fmtNum(value)}
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboards = () => {
  const cls = useStyles();
  const today = new Date();
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState({
    start: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
    end: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10)
  });
  const [sales, setSales] = useState(null);
  const [finance, setFinance] = useState(null);
  const [ops, setOps] = useState(null);
  const [goals, setGoals] = useState([]);

  const load = useCallback(async () => {
    const params = period;
    try {
      const [s, f, o, g] = await Promise.all([
        api.get("/dashboard/sales", { params }),
        api.get("/dashboard/finance", { params }),
        api.get("/dashboard/operations", { params }),
        api.get("/dashboard/goals")
      ]);
      setSales(s.data); setFinance(f.data); setOps(o.data); setGoals(g.data || []);
    } catch (err) { /* ignore */ }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  return (
    <MainContainer>
      <PageHeader
        title="Dashboards"
        subtitle="Indicadores consolidados do periodo escolhido, por area."
        filters={
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary"
              className={cls.tabs}>
              <Tab label="Comercial" />
              <Tab label="Financeiro" />
              <Tab label="Operações" />
              <Tab label="Metas" />
            </Tabs>
            <TextField type="date" label="Início" InputLabelProps={{ shrink: true }}
              value={period.start} onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))} />
            <TextField type="date" label="Fim" InputLabelProps={{ shrink: true }}
              value={period.end} onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))} />
          </>
        }
      />

      <Box mt={2}>
          {tab === 0 && sales && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><Stat label="Negócios em aberto" value={sales.counters.openDeals} /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Negócios ganhos" value={fmtNum(sales.counters.wonDeals)} delta={sales.deltas?.wonDeals} /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Negócios perdidos" value={fmtNum(sales.counters.lostDeals)} delta={sales.deltas?.lostDeals} invertColors /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Conversão" value={`${sales.conversionPct}%`} delta={sales.deltas?.conversionPct} /></Grid>
              <Grid item xs={12} md={6}><DeltaKpi label="Valor ganho no período" value={fmtMoney(sales.amounts.wonAmount)} delta={sales.deltas?.wonAmount} /></Grid>
              <Grid item xs={12} md={6}><Stat label="Valor em aberto" value={sales.amounts.openAmount} money /></Grid>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6">Por Vendedor</Typography>
                  <Table size="small"><TableHead><TableRow>
                    <TableCell>Vendedor</TableCell><TableCell>Qtd</TableCell><TableCell>Valor</TableCell>
                  </TableRow></TableHead>
                  <TableBody>{sales.byUser?.map((r, i) => (
                    <TableRow key={i}><TableCell>{r.ownerName || (r.ownerId ? `#${r.ownerId}` : "Sem responsável")}</TableCell>
                      <TableCell>{r.count}</TableCell><TableCell>{fmtMoney(r.amount)}</TableCell></TableRow>
                  ))}</TableBody></Table>
                </CardContent></Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card><CardContent>
                  <Typography variant="h6">Por Estágio (em aberto)</Typography>
                  <Table size="small"><TableHead><TableRow>
                    <TableCell>Estágio</TableCell><TableCell>Qtd</TableCell><TableCell>Valor</TableCell>
                  </TableRow></TableHead>
                  <TableBody>{sales.byStage?.map((r, i) => (
                    <TableRow key={i}><TableCell>{r.stageName || `#${r.stageId}`}</TableCell>
                      <TableCell>{r.count}</TableCell><TableCell>{fmtMoney(r.amount)}</TableCell></TableRow>
                  ))}</TableBody></Table>
                </CardContent></Card>
              </Grid>
            </Grid>
          )}

          {tab === 1 && finance && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><DeltaKpi label="Recebido" value={fmtMoney(finance.inflow)} delta={finance.deltas?.inflow} /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Pago" value={fmtMoney(finance.outflow)} delta={finance.deltas?.outflow} invertColors /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Saldo Líquido" value={fmtMoney(finance.netCashflow)} delta={finance.deltas?.netCashflow} /></Grid>
              <Grid item xs={6} md={3}><Stat label="A Receber Vencido" value={finance.pending.receivablesOverdue} money tone="negative" /></Grid>
              <Grid item xs={6} md={3}><Stat label="A Receber" value={finance.pending.receivables} money /></Grid>
              <Grid item xs={6} md={3}><Stat label="A Pagar" value={finance.pending.payables} money /></Grid>
              <Grid item xs={6} md={3}><Stat label="A Pagar Vencido" value={finance.pending.payablesOverdue} money tone="negative" /></Grid>
            </Grid>
          )}

          {tab === 2 && ops && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><Stat label="Tickets abertos" value={ops.tickets.open} /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Tickets fechados" value={fmtNum(ops.tickets.closedInPeriod)} delta={ops.deltas?.closedTickets} /></Grid>
              <Grid item xs={6} md={3}><Stat label="Tarefas abertas" value={ops.tasks.open} /></Grid>
              <Grid item xs={6} md={3}><DeltaKpi label="Tarefas concluídas" value={fmtNum(ops.tasks.doneInPeriod)} delta={ops.deltas?.tasksDone} /></Grid>
              <Grid item xs={6} md={3}><Stat label="Projetos ativos" value={ops.projects.active} /></Grid>
            </Grid>
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              {goals.map(g => {
                const pct = Math.min(100, g.percent || 0);
                return (
                  <Grid item xs={12} md={6} key={g.id}>
                    <Card><CardContent>
                      <Typography variant="subtitle1">{g.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {g.metric} · {g.startDate} → {g.endDate}
                      </Typography>
                      <LinearProgress variant="determinate" value={pct} className={cls.bar} />
                      <Typography variant="body2" style={{ marginTop: 4 }}>
                        {fmtNum(g.currentValue)} / {fmtNum(g.targetValue)} ({pct.toFixed(1)}%) — {g.status}
                      </Typography>
                    </CardContent></Card>
                  </Grid>
                );
              })}
              {goals.length === 0 && <Grid item xs={12}><Typography align="center">Nenhuma meta ativa</Typography></Grid>}
            </Grid>
          )}
      </Box>
    </MainContainer>
  );
};

export default Dashboards;
